/**
 * Stock / Equipment Tracker
 *
 * Shows what the confirmed pipeline needs vs what's on the shelf so the admin
 * can place orders before kit gaps stall installs. Three columns per row:
 *   Required (sum across confirmed orders) — In Stock (admin-editable) — Shortfall.
 *
 * "Confirmed" = deposit paid OR contract signed (server-side rule).
 * "Mark installed → deduct stock" lives on each customer row in the
 * confirmed-orders table; once clicked, the customer's equipment is subtracted
 * from in-stock and they drop off this view.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Package, Loader2, AlertTriangle, CheckCircle2, RefreshCw, Search, Save,
  Sun, Zap, Battery, ShieldCheck, Users, ShoppingCart, ChevronRight,
  Plus, Trash2, Wrench, Calendar, PlugZap, Wand2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const CATEGORY_META = {
  panel: { label: 'Panels', icon: Sun, color: 'text-amber-600' },
  inverter: { label: 'Inverters', icon: Zap, color: 'text-blue-600' },
  battery: { label: 'Batteries', icon: Battery, color: 'text-emerald-600' },
  ev_charger: { label: 'EV Chargers', icon: PlugZap, color: 'text-indigo-600' },
  consumer_unit: { label: 'Consumer Units', icon: ShieldCheck, color: 'text-slate-600' },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function StockPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tab, setTab] = useState('stock');
  const [savingRow, setSavingRow] = useState(null);
  const [draftQty, setDraftQty] = useState({}); // key → string while editing
  const [expandedRow, setExpandedRow] = useState(null);

  // Add new stock-only item (no current demand)
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ category: 'panel', manufacturer: '', model: '', in_stock_qty: 0 });

  // Confirm deduct stock
  const [deductOpen, setDeductOpen] = useState(null); // customer object

  // Map free-text demand → catalog SKU
  const [catalog, setCatalog] = useState([]);
  const [remapRow, setRemapRow] = useState(null);
  const [remapTarget, setRemapTarget] = useState('');
  const [remapping, setRemapping] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/stock/dashboard');
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load stock dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.get('/product-catalog').then((r) => setCatalog(r.data?.items || [])).catch(() => {});
  }, []);

  const catalogForCategory = (cat) => catalog.filter((c) => c.category === cat);

  const handleRemap = async () => {
    if (!remapRow || !remapTarget) return;
    const [to_manufacturer, to_model] = remapTarget.split('|||');
    setRemapping(true);
    try {
      const r = await api.post('/stock/remap', {
        category: remapRow.category,
        from_manufacturer: remapRow.manufacturer,
        from_model: remapRow.model || '',
        to_manufacturer,
        to_model: to_model || '',
      });
      toast.success(`Mapped — ${r.data?.updated || 0} job(s) updated`);
      setRemapRow(null);
      setRemapTarget('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to map product');
    } finally {
      setRemapping(false);
    }
  };

  const rowKey = (r) => `${r.category}|${r.manufacturer}|${r.model}`;

  const handleSaveStock = async (row) => {
    const key = rowKey(row);
    const qty = parseInt(draftQty[key] ?? row.in_stock, 10);
    if (Number.isNaN(qty) || qty < 0) {
      toast.error('Enter a valid non-negative number');
      return;
    }
    setSavingRow(key);
    try {
      await api.post('/stock/items', {
        category: row.category,
        manufacturer: row.manufacturer,
        model: row.model,
        in_stock_qty: qty,
      });
      toast.success(`Updated ${row.manufacturer} ${row.model || ''}`);
      setDraftQty((d) => { const n = { ...d }; delete n[key]; return n; });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSavingRow(null); }
  };

  const handleAddStock = async () => {
    if (!addForm.manufacturer.trim()) {
      toast.error('Manufacturer is required');
      return;
    }
    try {
      await api.post('/stock/items', { ...addForm, in_stock_qty: parseInt(addForm.in_stock_qty, 10) || 0 });
      toast.success('Stock item added');
      setAddOpen(false);
      setAddForm({ category: 'panel', manufacturer: '', model: '', in_stock_qty: 0 });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add');
    }
  };

  const handleDeleteStock = async (row) => {
    if (!row.id) return;
    if (!confirm(`Remove ${row.manufacturer} ${row.model} from stock tracking?`)) return;
    try {
      await api.delete(`/stock/items/${row.id}`);
      toast.success('Removed from tracking');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  const handleDeduct = async (customer) => {
    try {
      const r = await api.post(`/stock/deduct/${customer.id}`);
      toast.success(`Stock deducted for ${customer.name} (${r.data?.lines?.length || 0} lines)`);
      setDeductOpen(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not deduct stock');
    }
  };

  // ─── Filtering ───
  const filteredItems = useMemo(() => {
    const items = data?.items || [];
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      if (categoryFilter !== 'all' && r.category !== categoryFilter) return false;
      if (q && !`${r.manufacturer} ${r.model}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, query, categoryFilter]);

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  }
  if (!data) return <Layout><p className="p-8 text-slate-500">Could not load stock data.</p></Layout>;

  const summary = data.summary || {};

  return (
    <Layout>
      <div className="space-y-5 max-w-7xl mx-auto" data-testid="stock-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 font-['Manrope']">
              <Package className="w-6 h-6 text-blue-600" />
              Stock
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Confirmed orders (deposit paid OR contract signed) and what kit they need vs what's on the shelf.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refresh} data-testid="stock-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-stock-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add stock item
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Confirmed orders</p>
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="summary-confirmed">{summary.confirmed_orders || 0}</p>
              <p className="text-xs text-slate-500">awaiting installation</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Items tracked</p>
                <Package className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="summary-items">{summary.items_tracked || 0}</p>
              <p className="text-xs text-slate-500">SKUs in pipeline</p>
            </CardContent>
          </Card>
          <Card className={summary.items_short > 0 ? 'border-rose-200 bg-rose-50/40' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Items short</p>
                <AlertTriangle className={`w-4 h-4 ${summary.items_short > 0 ? 'text-rose-600' : 'text-slate-400'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${summary.items_short > 0 ? 'text-rose-700' : 'text-slate-900'}`} data-testid="summary-short">{summary.items_short || 0}</p>
              <p className="text-xs text-slate-500">need ordering</p>
            </CardContent>
          </Card>
          <Card className={summary.total_to_order > 0 ? 'border-amber-200 bg-amber-50/40' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Units to order</p>
                <ShoppingCart className={`w-4 h-4 ${summary.total_to_order > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${summary.total_to_order > 0 ? 'text-amber-700' : 'text-slate-900'}`} data-testid="summary-to-order">{summary.total_to_order || 0}</p>
              <p className="text-xs text-slate-500">total deficit</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="stock" data-testid="tab-stock"><Package className="w-3.5 h-3.5 mr-1" /> Stock Levels</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><Users className="w-3.5 h-3.5 mr-1" /> Confirmed Orders ({summary.confirmed_orders || 0})</TabsTrigger>
          </TabsList>

          {/* Stock Levels Tab */}
          <TabsContent value="stock" className="space-y-3 mt-4">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter by manufacturer or model…"
                  className="pl-9"
                  data-testid="stock-search"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCategoryFilter('all')}
                  className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${categoryFilter === 'all' ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  data-testid="stock-cat-all"
                >All</button>
                {Object.entries(CATEGORY_META).map(([k, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <button
                      key={k}
                      onClick={() => setCategoryFilter(k)}
                      className={`flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${categoryFilter === k ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      data-testid={`stock-cat-${k}`}
                    >
                      <Icon className={`w-3 h-3 ${meta.color}`} /> {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stock table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left">
                        <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 w-8"></th>
                        <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">Item</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 text-right w-24">Required</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 text-right w-32">In stock</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 text-right w-28">Shortfall</th>
                        <th className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 text-right w-32"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">No stock items match this filter.</td></tr>
                      ) : filteredItems.map((row) => {
                        const meta = CATEGORY_META[row.category] || { label: row.category, icon: Package, color: 'text-slate-500' };
                        const Icon = meta.icon;
                        const key = rowKey(row);
                        const expanded = expandedRow === key;
                        const editingQty = draftQty[key];
                        return (
                          <React.Fragment key={key}>
                            <tr className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`stock-row-${key.replace(/[^a-z0-9]/gi, '-')}`}>
                              <td className="px-3 py-2.5">
                                {row.allocated_to.length > 0 && (
                                  <button
                                    onClick={() => setExpandedRow(expanded ? null : key)}
                                    className="text-slate-400 hover:text-slate-700"
                                    data-testid={`expand-row-${key.replace(/[^a-z0-9]/gi, '-')}`}
                                    aria-label="Show customers"
                                  >
                                    <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-2">
                                  <Icon className={`w-4 h-4 ${meta.color}`} />
                                  <div className="min-w-0">
                                    <p className="font-medium text-slate-900 truncate">{row.manufacturer}</p>
                                    <p className="text-xs text-slate-500 truncate">{row.model || <em>no model</em>}</p>
                                    {!row.in_catalog && (
                                      <button
                                        onClick={() => { setRemapRow(row); setRemapTarget(''); }}
                                        className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 hover:bg-amber-100"
                                        data-testid={`map-catalog-${key.replace(/[^a-z0-9]/gi, '-')}`}
                                        title="This product isn't in the catalog — map it to a catalog SKU"
                                      >
                                        <Wand2 className="w-3 h-3" /> Not in catalog · Map
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{row.required}</td>
                              <td className="px-3 py-2.5 text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  <Input
                                    type="number"
                                    min={0}
                                    value={editingQty !== undefined ? editingQty : row.in_stock}
                                    onChange={(e) => setDraftQty((d) => ({ ...d, [key]: e.target.value }))}
                                    className="w-20 h-8 text-right tabular-nums"
                                    data-testid={`stock-qty-${key.replace(/[^a-z0-9]/gi, '-')}`}
                                  />
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                {row.shortfall > 0 ? (
                                  <Badge className="bg-rose-100 text-rose-700 border-rose-200 border tabular-nums">{row.shortfall}</Badge>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border tabular-nums">0</Badge>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {editingQty !== undefined && parseInt(editingQty, 10) !== row.in_stock && (
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveStock(row)}
                                      disabled={savingRow === key}
                                      className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                                      data-testid={`save-row-${key.replace(/[^a-z0-9]/gi, '-')}`}
                                    >
                                      {savingRow === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                    </Button>
                                  )}
                                  {row.id && row.required === 0 && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDeleteStock(row)}
                                      className="h-7 text-xs text-rose-600 hover:bg-rose-50"
                                      data-testid={`delete-row-${key.replace(/[^a-z0-9]/gi, '-')}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {expanded && row.allocated_to.length > 0 && (
                              <tr className="bg-slate-50/70">
                                <td colSpan={6} className="px-12 py-2">
                                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Allocated to</p>
                                  <div className="space-y-1">
                                    {row.allocated_to.map((a, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs gap-3" data-testid={`alloc-${i}`}>
                                        <Link to={`/customers/${a.customer_id}`} className="font-medium text-blue-700 hover:underline truncate">{a.customer_name}</Link>
                                        <div className="flex items-center gap-3 text-slate-500 flex-shrink-0">
                                          {a.confirmed_install_date && (
                                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(a.confirmed_install_date)}</span>
                                          )}
                                          <span className="tabular-nums">× {a.qty}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Confirmed Orders Tab */}
          <TabsContent value="orders" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" /> Confirmed orders awaiting installation
                </CardTitle>
                <p className="text-xs text-slate-500">
                  Customers who've paid a deposit OR signed a contract. When the install is done, click <strong>Mark installed</strong> to deduct their equipment from stock.
                </p>
              </CardHeader>
              <CardContent className="space-y-2">
                {(data.confirmed_orders || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-6 text-center">No confirmed orders.</p>
                ) : data.confirmed_orders.map((c) => (
                  <div key={c.id} className="p-3 rounded border border-slate-200 hover:border-slate-300 transition-colors" data-testid={`order-row-${c.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/customers/${c.id}`} className="font-semibold text-blue-700 hover:underline">{c.name || c.email}</Link>
                          {c.status && <Badge variant="outline" className="text-[10px] capitalize">{(c.status || '').replace(/_/g, ' ')}</Badge>}
                          {c.deposit_paid_date && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[10px]">Deposit · {fmtDate(c.deposit_paid_date)}</Badge>}
                          {c.confirmed_install_date && <Badge className="bg-blue-100 text-blue-700 border-blue-200 border text-[10px]"><Calendar className="w-3 h-3 mr-0.5" /> Install · {fmtDate(c.confirmed_install_date)}</Badge>}
                          {!c.confirmed_install_date && c.provisional_install_date && <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]"><Calendar className="w-3 h-3 mr-0.5" /> Prov · {fmtDate(c.provisional_install_date)}</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{c.email}</p>
                        {c.equipment.length === 0 ? (
                          <p className="text-xs text-amber-700 mt-1.5 italic flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No equipment recorded — fill the Equipment card before deducting stock.</p>
                        ) : (
                          <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5">
                            {c.equipment.map((e, i) => {
                              const meta = CATEGORY_META[e.category] || {};
                              const Icon = meta.icon || Package;
                              return (
                                <div key={i} className="text-xs flex items-center gap-1.5 bg-slate-50 rounded px-2 py-1" data-testid={`order-equip-${c.id}-${e.category}`}>
                                  <Icon className={`w-3 h-3 flex-shrink-0 ${meta.color}`} />
                                  <span className="truncate" title={`${e.manufacturer} ${e.model}`}>
                                    {e.manufacturer}{e.model ? ` ${e.model}` : ''} <span className="text-slate-400">× {e.qty}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {c.equipment.length > 0 && (
                        <Button
                          size="sm"
                          onClick={() => setDeductOpen(c)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
                          data-testid={`deduct-btn-${c.id}`}
                        >
                          <Wrench className="w-3.5 h-3.5 mr-1" /> Mark installed → deduct stock
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Map free-text demand → catalog SKU */}
      <Dialog open={!!remapRow} onOpenChange={(o) => { if (!o) { setRemapRow(null); setRemapTarget(''); } }}>
        <DialogContent className="max-w-md" data-testid="remap-dialog">
          <DialogHeader>
            <DialogTitle>Map to a catalog product</DialogTitle>
            <DialogDescription>
              {remapRow && (
                <>Consolidate demand for <strong>{remapRow.manufacturer}{remapRow.model ? ` — ${remapRow.model}` : ''}</strong> onto an exact catalog product. Every job using the old name will be updated.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-xs text-slate-500 mb-1 block">Catalog product ({remapRow ? (CATEGORY_META[remapRow.category]?.label || remapRow.category) : ''})</label>
            <select
              value={remapTarget}
              onChange={(e) => setRemapTarget(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
              data-testid="remap-target-select"
            >
              <option value="">Select a catalog product…</option>
              {remapRow && catalogForCategory(remapRow.category).map((c) => (
                <option key={c.id} value={`${c.manufacturer}|||${c.model || ''}`}>
                  {c.manufacturer}{c.model ? ` — ${c.model}` : ''}
                </option>
              ))}
            </select>
            {remapRow && catalogForCategory(remapRow.category).length === 0 && (
              <p className="text-xs text-amber-600 mt-2">No catalog products in this section yet — add some in the Product Catalog first.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRemapRow(null); setRemapTarget(''); }}>Cancel</Button>
            <Button onClick={handleRemap} disabled={!remapTarget || remapping} className="bg-blue-600 hover:bg-blue-700" data-testid="remap-confirm-btn">
              {remapping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />} Map &amp; update jobs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Add stock item dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-stock-dialog">
          <DialogHeader>
            <DialogTitle>Add stock item</DialogTitle>
            <DialogDescription>Track an item that isn't currently demanded by a confirmed order — useful for parts you hold on the shelf.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Category</label>
              <select
                value={addForm.category}
                onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
                className="w-full h-9 px-2 text-sm border border-slate-200 rounded"
                data-testid="add-stock-category"
              >
                {Object.entries(CATEGORY_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Manufacturer <span className="text-rose-500">*</span></label>
              <Input value={addForm.manufacturer} onChange={(e) => setAddForm({ ...addForm, manufacturer: e.target.value })} placeholder="e.g. Aiko Energy" data-testid="add-stock-manu" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Model</label>
              <Input value={addForm.model} onChange={(e) => setAddForm({ ...addForm, model: e.target.value })} placeholder="e.g. AIKO-A490-MCE54Mb" data-testid="add-stock-model" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">In stock</label>
              <Input type="number" min={0} value={addForm.in_stock_qty} onChange={(e) => setAddForm({ ...addForm, in_stock_qty: e.target.value })} data-testid="add-stock-qty" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddStock} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-stock-confirm-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm deduct dialog */}
      <Dialog open={!!deductOpen} onOpenChange={(o) => { if (!o) setDeductOpen(null); }}>
        <DialogContent className="max-w-md" data-testid="deduct-stock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wrench className="w-5 h-5 text-emerald-600" /> Mark installation complete</DialogTitle>
            <DialogDescription>This will subtract the equipment below from your stock count and remove this customer from the confirmed-orders list.</DialogDescription>
          </DialogHeader>
          {deductOpen && (
            <div className="space-y-2 py-2">
              <p className="text-sm font-medium text-slate-900">{deductOpen.name}</p>
              <p className="text-xs text-slate-500">{deductOpen.email}</p>
              <div className="space-y-1 mt-3">
                {deductOpen.equipment.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2.5 py-1.5">
                    <span>{e.manufacturer} {e.model}</span>
                    <span className="tabular-nums font-semibold">× {e.qty}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeductOpen(null)}>Cancel</Button>
            <Button onClick={() => handleDeduct(deductOpen)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-deduct-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Confirm — deduct stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
